import { Component, ElementRef, Inject, Optional, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { CallerService } from '../../services/caller.service'
import { HttpStatusCode } from '@angular/common/http'
import { AdminService } from '../../services/admin.service'
import { AnalyticsService } from '../../services/analytics.service'
import { HAS_POSTED_BEFORE_KEY, PushNotificationService } from '../../services/push-notification.service'

import { SALE_CATEGORIES, SALE_CONDITIONS } from '../../utils/sale.util'
import { amountValidator, focusFirstInvalid, notMoreThanValidator } from '../../utils/form-validation.util'

/** A photo picked for the sale, with its preview. */
interface PhotoDraft {
    file: File
    dataUrl: string
}

/** The backend takes up to 12 files a post. */
export const MAX_SALE_PHOTOS = 12

/** The backend's item_name column is a VARCHAR(255). */
export const ITEM_NAME_MAX_LENGTH = 255

/** The photos in upload order: the cover first (the backend makes the first upload the cover). */
export function photosInUploadOrder<T>(photos: T[], coverIndex: number): T[] {
    const cover = photos[coverIndex]
    return cover ? [cover, ...photos.filter((_, i) => i !== coverIndex)] : photos
}

@Component({
    selector: 'app-create-new-sale',
    templateUrl: './create-new-sale.component.html',
    styleUrls: ['./create-new-sale.component.css'],
})
export class CreateNewSaleComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>()

    submittingSale: boolean = false;
    @ViewChild('imgFileInput', { read: ElementRef<HTMLInputElement> })
    imgFileInput!: ElementRef<HTMLInputElement>

    constructor(
        private _formBuilder: FormBuilder,
        private createPostDialogRef: MatDialogRef<CreateNewSaleComponent>,
        private callerService: CallerService,
        @Optional() @Inject(MAT_DIALOG_DATA) public data: { isAdmin?: boolean } | null,
        private adminService: AdminService,
        private analyticsService: AnalyticsService,
        private pushNotificationService: PushNotificationService,
        private host: ElementRef<HTMLElement>,
    ) {}

    readonly conditions = SALE_CONDITIONS
    readonly categories = SALE_CATEGORIES
    readonly maxPhotos = MAX_SALE_PHOTOS
    readonly itemNameMaxLength = ITEM_NAME_MAX_LENGTH

    photos: PhotoDraft[] = []
    /** Which photo is the cover - the one on the card. The first picked, unless they choose another. */
    coverIndex = 0
    /** Set when they try to post without one - buyers skip listings with no photo. */
    photoMissing = false

    /** Admin posts (announcements) can go without a photo, condition or category. */
    get isAdminPost(): boolean {
        return !!this.data?.isAdmin
    }

    saleFormGroup: FormGroup = new FormGroup({
        price: new FormControl('', [Validators.required, amountValidator(1)]),
        minimum_price: new FormControl(''),
        is_item_negotiable: new FormControl(false),
        text: new FormControl(''),
        item_name: new FormControl('', [
            Validators.required,
            Validators.minLength(3),
            Validators.maxLength(ITEM_NAME_MAX_LENGTH),
        ]),
        condition: new FormControl<string | null>(null),
        category: new FormControl<string | null>(null),
    })

    ngOnInit(): void {
        if (!this.isAdminPost) {
            this.saleFormGroup.get('condition')?.addValidators(Validators.required)
            this.saleFormGroup.get('category')?.addValidators(Validators.required)
        }

        const minimumPrice = this.saleFormGroup.get('minimum_price')!
        // The lowest price only counts (and is only shown) for a negotiable item.
        this.saleFormGroup.get('is_item_negotiable')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((negotiable: boolean) => {
            if (negotiable) {
                minimumPrice.setValidators([Validators.required, amountValidator(1), notMoreThanValidator('price')])
            } else {
                minimumPrice.clearValidators()
                minimumPrice.setValue(null)
            }
            minimumPrice.updateValueAndValidity()
        })
        // It can't be more than the price, so check it again when the price changes.
        this.saleFormGroup.get('price')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() =>
            minimumPrice.updateValueAndValidity({ emitEvent: false }),
        )
    }

    ngOnDestroy(): void {
        this.destroy$.next()
        this.destroy$.complete()
    }

    /** Whether to show `control`'s `error` (any error, without one): after they've touched it or tried to post. */
    showError(control: string, error?: string): boolean {
        const c = this.saleFormGroup.get(control)
        return !!c && c.touched && (error ? c.hasError(error) : c.invalid)
    }

    /** The form's fields, then the photos - cover first. */
    buildFormData(): FormData {
        const formData = new FormData()
        for (const field in this.saleFormGroup.controls) {
            const value = this.saleFormGroup.get(field)?.value
            // The backend reads "null" as empty, but leaving it out is clearer.
            if (value !== null && value !== undefined) formData.set(field, value)
        }
        photosInUploadOrder(this.photos, this.coverIndex).forEach((photo, i) =>
            formData.append(`sale-media-${i}`, photo.file, photo.file.name),
        )
        return formData
    }

    /** Clears the form and the photos, ready for the next item. */
    clearFormDataAndImages(): void {
        // reset() also leaves it untouched, so no errors show - but keeps them, so an empty form can't be posted.
        this.saleFormGroup.reset({ is_item_negotiable: false })
        this.photos = []
        this.coverIndex = 0
        this.photoMissing = false
        if (this.imgFileInput) this.imgFileInput.nativeElement.value = ''
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement
        // TODO: allow video (file.type 'video/*') once video uploads go to YouTube or a similar video
        // service - we need video support. Photos only until then (see the input's accept in the template).
        const files = Array.from(input.files ?? []).filter((file) => file.type.startsWith('image/'))
        const room = this.maxPhotos - this.photos.length
        if (files.length > room) {
            this.callerService.showNotification(`Up to ${this.maxPhotos} photos - the first ${Math.max(room, 0)} were added.`)
        }
        files.slice(0, Math.max(room, 0)).forEach((file) => this.addPhoto(file))
        // So picking the same file again (after removing it) still fires (change).
        input.value = ''
    }

    private addPhoto(file: File) {
        const reader = new FileReader()
        reader.onerror = () => this.callerService.showNotification(`Couldn't read ${file.name}. Try another photo.`)
        reader.onload = () => {
            this.photos = [...this.photos, { file, dataUrl: String(reader.result) }]
            this.photoMissing = false
        }
        reader.readAsDataURL(file)
    }

    setCover(index: number) {
        this.coverIndex = index
    }

    removePhoto(index: number) {
        this.photos = this.photos.filter((_, i) => i !== index)
        // Keep the same photo as the cover, or fall back to the first.
        if (index === this.coverIndex) this.coverIndex = 0
        else if (index < this.coverIndex) this.coverIndex--
    }

    submit(): void {
        this.photoMissing = !this.isAdminPost && this.photos.length === 0
        if (this.saleFormGroup.valid && !this.photoMissing) {
            const formData = this.buildFormData()

            this.submittingSale = true
            const isAdmin = this.data?.isAdmin && this.adminService?.isAdminLoggedIn
            const req$ = isAdmin
                ? this.adminService.createSalePost(formData)
                : this.callerService.createNewSalePost(formData)
            req$.subscribe({
                next: (res: any) => {
                    if (res.status === HttpStatusCode.Ok) {
                        this.analyticsService.postCreated('sale')
                        this.callerService.showNotification(isAdmin ? 'Sale posted (admin)' : 'Sale posted')
                        this.clearFormDataAndImages()
                        if (isAdmin) {
                            this.createPostDialogRef.close(true)
                        } else if (!localStorage.getItem(HAS_POSTED_BEFORE_KEY)) {
                            localStorage.setItem(HAS_POSTED_BEFORE_KEY, 'true')
                            this.pushNotificationService.maybePromptToEnable()
                        }
                    } else {
                        this.callerService.showNotification('An error occurred')
                    }
                    this.submittingSale = false
                },
                error: (err) => {
                    this.submittingSale = false
                    this.callerService.showNotification(err?.error?.message ?? 'An error occurred')
                },
            })
        } else {
            // Show every error where it is, and take them to the first one.
            this.saleFormGroup.markAllAsTouched()
            setTimeout(() => focusFirstInvalid(this.host.nativeElement))
            this.callerService.showNotification(this.photoMissing && this.saleFormGroup.valid ? 'Add at least one photo' : 'Check the fields marked in red')
        }
    }

    close(): void {
        this.createPostDialogRef.close()
    }

    saveDraft() {
        
    }
}
